import { useCallback, useEffect, useMemo, useState } from 'react';
import { Anchor, Download, Search, ChevronRight, ArrowRightLeft } from 'lucide-react';
import DamenNaarUrenlijst from './DamenNaarUrenlijst';
import DamenKoppeling from './DamenKoppeling';
import { fold } from '../../utils/zoek';
import './DamenUren.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const DAGEN = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const KLEUREN = ['#e65100', '#2f6f8f', '#7a8b2b', '#8a4fa8', '#c2185b', '#00897b', '#6d4c41', '#546e7a'];

// "Overtime weekday - Ozel veiligheid DSR Nieuw" → "Overtime weekday"
const kort = (t) => String(t).split(' - ')[0].trim();
const uur = (n) => (Math.round(n * 100) / 100).toLocaleString('nl-NL', { maximumFractionDigits: 2 });
const datumNL = (s) => { const [y, m, d] = s.split('-'); return `${d}-${m}-${y}`; };

// Dagen van de week (ma t/m zo) vanaf de maandag van die week
function weekDagen(jaar, week) {
  const jan4 = new Date(Date.UTC(jaar, 0, 4));
  const ma = new Date(jan4);
  ma.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (week - 1) * 7);
  return DAGEN.map((_, i) => { const d = new Date(ma); d.setUTCDate(ma.getUTCDate() + i); return d.toISOString().slice(0, 10); });
}

const PROFIELEN = [
  { key: 'ozel', naam: 'Özel' },
  { key: 'entice', naam: 'Entice' },
  { key: 'myform', naam: 'MyForm' },
];
const bewaardProfiel = () => { try { const p = localStorage.getItem('damen_profiel'); return PROFIELEN.some((x) => x.key === p) ? p : 'ozel'; } catch { return 'ozel'; } };

export default function DamenUren() {
  const [profiel, setProfielRaw] = useState(bewaardProfiel);
  const [naarLijst, setNaarLijst] = useState(false);
  const [weken, setWeken] = useState(null);
  const [keuze, setKeuze] = useState('');
  const [data, setData] = useState(null);
  const [zoek, setZoek] = useState('');
  const [sort, setSort] = useState('naam');
  const [open, setOpen] = useState(null);
  const [versie, setVersie] = useState(0);

  useEffect(() => {
    authFetch(`/api/damen-uren/weken?profiel=${profiel}`).then((r) => (r.ok ? r.json() : [])).then((w) => {
      setWeken(w);
      setKeuze((k) => (k && w.some((x) => `${x.jaar}-${x.week}` === k) ? k : (w.length ? `${w[0].jaar}-${w[0].week}` : '')));
    }).catch(() => setWeken([]));
  }, [versie, profiel]);

  const setProfiel = (p) => {
    setProfielRaw(p); setWeken(null); setData(null); setKeuze('');
    try { localStorage.setItem('damen_profiel', p); } catch { /* geen opslag */ }
  };
  const profielNaam = PROFIELEN.find((x) => x.key === profiel)?.naam;

  const herlaad = useCallback(() => setVersie((v) => v + 1), []);

  useEffect(() => {
    if (!keuze) return;
    const [jaar, week] = keuze.split('-');
    setData(null); setOpen(null);
    authFetch(`/api/damen-uren?profiel=${profiel}&jaar=${jaar}&week=${week}`).then((r) => (r.ok ? r.json() : [])).then(setData).catch(() => setData([]));
  }, [keuze, versie, profiel]);

  const [jaar, week] = keuze ? keuze.split('-').map(Number) : [0, 0];
  const dagen = useMemo(() => (jaar ? weekDagen(jaar, week) : []), [jaar, week]);

  // Tarieven in vaste volgorde (laag → hoog percentage) met een eigen kleur
  const tarieven = useMemo(() => {
    const m = new Map();
    for (const t of data || []) for (const r of t.regels) {
      const k = kort(r.tarief);
      if (!m.has(k)) m.set(k, { naam: k, percentage: r.percentage, uren: 0 });
      m.get(k).uren += r.uren;
    }
    return [...m.values()].sort((a, b) => a.percentage - b.percentage || a.naam.localeCompare(b.naam))
      .map((t, i) => ({ ...t, kleur: KLEUREN[i % KLEUREN.length] }));
  }, [data]);
  const kleurVan = useMemo(() => Object.fromEntries(tarieven.map((t) => [t.naam, t.kleur])), [tarieven]);

  // Werkt iemand op meerdere locaties, dan heeft hij meerdere urenstaten. Die tonen we als één persoon.
  const personen = useMemo(() => {
    const m = new Map();
    for (const t of data || []) {
      const p = m.get(t.medewerker);
      if (!p) { m.set(t.medewerker, { ...t, staten: [t] }); continue; }
      p.staten.push(t);
      p.totaal += t.totaal;
      p.regels = [...p.regels, ...t.regels];
      p.ts = p.staten.map((x) => x.ts).join(', ');
      p.opdracht = p.staten.map((x) => x.opdracht).join(', ');
    }
    return [...m.values()];
  }, [data]);

  const rijen = useMemo(() => {
    const q = fold(zoek).trim();
    const list = personen.filter((t) => !q || fold(`${t.medewerker} ${t.ts} ${t.opdracht} ${t.functie}`).includes(q)).map((t) => {
      const perDag = dagen.map((d) => t.regels.filter((r) => r.datum === d).reduce((a, r) => a + r.uren, 0));
      const perTarief = {};
      for (const r of t.regels) perTarief[kort(r.tarief)] = (perTarief[kort(r.tarief)] || 0) + r.uren;
      return { ...t, perDag, perTarief };
    });
    return list.sort((a, b) => (sort === 'uren' ? b.totaal - a.totaal : a.medewerker.localeCompare(b.medewerker)));
  }, [personen, zoek, sort, dagen]);

  const totaal = personen.reduce((a, t) => a + t.totaal, 0);
  const meta = weken?.find((w) => `${w.jaar}-${w.week}` === keuze);

  const exportCsv = () => {
    const kop = ['Medewerker', 'Urenstaat', 'Opdracht', 'Functie', ...DAGEN, ...tarieven.map((t) => `${t.naam} (${t.percentage}%)`), 'Totaal'];
    const regels = rijen.map((r) => [r.medewerker, r.ts, r.opdracht, r.functie, ...r.perDag.map(uur), ...tarieven.map((t) => uur(r.perTarief[t.naam] || 0)), uur(r.totaal)]);
    const csv = [kop, ...regels].map((l) => l.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `damen-uren-week-${week}-${jaar}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const kop = (
    <>
      <header className="du-head">
        <div>
          <h2><Anchor size={20} /> Damen uren</h2>
          <p>Uren zoals Damen ze in FlexDamen heeft vastgelegd. Damen Shiprepair Amsterdam telt niet mee.</p>
        </div>
        <div className="du-profielen" role="tablist" aria-label="Bedrijf">
          {PROFIELEN.map((p) => (
            <button key={p.key} type="button" role="tab" aria-selected={profiel === p.key} className={profiel === p.key ? 'is-actief' : ''} onClick={() => setProfiel(p.key)}>{p.naam}</button>
          ))}
        </div>
      </header>
      <DamenKoppeling key={profiel} profiel={profiel} profielNaam={profielNaam} opgeslagen={weken || []} onKlaar={herlaad} />
    </>
  );

  if (weken === null) return <div className="du">{kop}<p className="du-empty">Laden…</p></div>;
  if (!weken.length) return <div className="du">{kop}<p className="du-empty">Nog geen Damen-uren opgeslagen voor {profielNaam}.</p></div>;

  return (
    <div className="du">
      {kop}
      <div className="du-weekbalk">
        <div className="du-controls">
          <label className="du-select">
            <span>Week</span>
            <select value={keuze} onChange={(e) => setKeuze(e.target.value)}>
              {weken.map((w) => <option key={`${w.jaar}-${w.week}`} value={`${w.jaar}-${w.week}`}>Week {w.week} · {w.jaar}</option>)}
            </select>
          </label>
          <button type="button" className="du-btn" onClick={exportCsv} disabled={!rijen.length}><Download size={15} /> Excel (CSV)</button>
          <button type="button" className="du-btn du-btn-primary" onClick={() => setNaarLijst(true)} disabled={!data?.length}><ArrowRightLeft size={15} /> Naar urenlijst</button>
        </div>
      </div>
      {naarLijst && <DamenNaarUrenlijst profiel={profiel} profielNaam={profielNaam} jaar={jaar} week={week} onSluit={() => setNaarLijst(false)} />}

      {data === null ? <p className="du-empty">Laden…</p> : (
        <>
          <section className="du-kpis">
            <div className="du-kpi du-kpi--main"><span>Totaal uren</span><strong>{uur(totaal)}</strong><small>{dagen.length ? `${datumNL(dagen[0])} t/m ${datumNL(dagen[6])}` : ''}</small></div>
            <div className="du-kpi"><span>Medewerkers</span><strong>{personen.length}</strong><small>{data.length > personen.length ? `${data.length} urenstaten` : 'urenstaten'}</small></div>
            <div className="du-kpi"><span>Gemiddeld</span><strong>{uur(personen.length ? totaal / personen.length : 0)}</strong><small>uur per medewerker</small></div>
            <div className="du-kpi"><span>Bijgewerkt</span><strong className="du-kpi-sm">{meta?.opgehaald ? new Date(meta.opgehaald).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '—'}</strong><small>{meta?.opgehaald ? new Date(meta.opgehaald).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : ''}</small></div>
          </section>

          <section className="du-card">
            <h3>Uren per tarief</h3>
            <div className="du-stack" role="img" aria-label="Verdeling van uren per tarief">
              {tarieven.map((t) => <span key={t.naam} style={{ width: `${(t.uren / (totaal || 1)) * 100}%`, background: t.kleur }} title={`${t.naam}: ${uur(t.uren)} uur`} />)}
            </div>
            <ul className="du-legend">
              {tarieven.map((t) => (
                <li key={t.naam}>
                  <i style={{ background: t.kleur }} />
                  <span>{t.naam} <em>{t.percentage}%</em></span>
                  <strong>{uur(t.uren)}</strong>
                  <small>{Math.round((t.uren / (totaal || 1)) * 100)}%</small>
                </li>
              ))}
            </ul>
          </section>

          <section className="du-card">
            <div className="du-tablehead">
              <h3>Per medewerker</h3>
              <div className="du-tools">
                <label className="du-search"><Search size={15} /><input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek naam, TS of opdracht" /></label>
                <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sorteren">
                  <option value="naam">Op naam</option>
                  <option value="uren">Meeste uren</option>
                </select>
              </div>
            </div>
            <div className="du-scroll">
              <table className="du-table">
                <thead>
                  <tr>
                    <th className="du-name">Medewerker</th>
                    {DAGEN.map((d, i) => <th key={d} className={i > 4 ? 'is-we' : ''}>{d}<small>{dagen[i]?.slice(8, 10)}</small></th>)}
                    <th>Totaal</th>
                    <th className="du-split-h">Verdeling</th>
                  </tr>
                </thead>
                <tbody>
                  {rijen.map((r) => (
                    <FragmentRij key={r.id} r={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} dagen={dagen} tarieven={tarieven} kleurVan={kleurVan} />
                  ))}
                  {!rijen.length && <tr><td colSpan={10} className="du-empty">Niemand gevonden.</td></tr>}
                </tbody>
                {rijen.length > 0 && (
                  <tfoot>
                    <tr>
                      <td className="du-name">Totaal</td>
                      {dagen.map((d, i) => <td key={d} className={i > 4 ? 'is-we' : ''}>{uur(rijen.reduce((a, r) => a + r.perDag[i], 0))}</td>)}
                      <td><strong>{uur(rijen.reduce((a, r) => a + r.totaal, 0))}</strong></td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function FragmentRij({ r, open, onToggle, dagen, tarieven, kleurVan }) {
  return (
    <>
      <tr className={`du-row${open ? ' is-open' : ''}`} onClick={onToggle}>
        <td className="du-name">
          <button type="button" aria-expanded={open} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
            <ChevronRight size={15} className="du-chev" />
            <span><strong>{r.medewerker}{r.staten.length > 1 && <span className="du-badge-loc">{r.staten.length} locaties</span>}</strong><small>{r.functie} · {r.ts}</small></span>
          </button>
        </td>
        {r.perDag.map((u, i) => <td key={i} className={`${i > 4 ? 'is-we' : ''}${u ? '' : ' is-zero'}`}>{u ? uur(u) : '–'}</td>)}
        <td><strong>{uur(r.totaal)}</strong></td>
        <td className="du-split">
          <div className="du-mini" title={tarieven.filter((t) => r.perTarief[t.naam]).map((t) => `${t.naam}: ${uur(r.perTarief[t.naam])}`).join('\n')}>
            {tarieven.map((t) => r.perTarief[t.naam] ? <span key={t.naam} style={{ width: `${(r.perTarief[t.naam] / r.totaal) * 100}%`, background: kleurVan[t.naam] }} /> : null)}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="du-detail">
          <td colSpan={10}>
            <div className="du-detail-in">
              <table>
                <tbody>
                  {tarieven.filter((t) => r.perTarief[t.naam]).map((t) => (
                    <tr key={t.naam}>
                      <th><i style={{ background: t.kleur }} />{t.naam} <em>{t.percentage}%</em></th>
                      {dagen.map((d) => {
                        const u = r.regels.filter((x) => x.datum === d && kort(x.tarief) === t.naam).reduce((a, x) => a + x.uren, 0);
                        return <td key={d}>{u ? uur(u) : ''}</td>;
                      })}
                      <td><strong>{uur(r.perTarief[t.naam])}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {r.staten.length > 1 ? (
                <ul className="du-locs">
                  {r.staten.map((t) => <li key={t.id}><strong>{t.klant}</strong> · {uur(t.totaal)} uur · {t.ts} · opdracht {t.opdracht} · PO {t.po || '—'}</li>)}
                </ul>
              ) : <p>{r.klant} · opdracht {r.opdracht} · {r.afdeling} · PO {r.po || '—'} · {r.status}</p>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
